import React, { useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, Link, generatePath, useRouteMatch, useLocation } from 'react-router-dom';
import Bluebird from 'bluebird';
import { Breadcrumbs, Paper, Typography } from '@mui/material';
import { confirmAlert } from 'react-confirm-alert';
import routes from '../routes';
import * as foodDonorsActions from '../actions/foodDonors';
import OverlayLoader from '../components/OverlayLoader';
import FoodDonorPickupLocationAddForm from '../components/FoodDonorPickupLocationAddForm';
import NotificationsContext from '../context/NotificationsContext/NotificationsContext';
import * as notificationsHelper from '../helpers/notifications';
import usePermission from '../hooks/usePermission';
import ACLService from '../services/acl';
import ConfirmationDialog from '../components/ConfirmationDialog';

const FoodDonorPickupLocationAddView = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const match = useRouteMatch();
  const location = useLocation();
  const { state } = location;
  const locationId = match.params.foodDonorId;
  const foodDonors = useSelector(state => state.entities.foodDonors);
  const foodDonor = foodDonors.byId[locationId];
  const notificationsContext = useContext(NotificationsContext);
  const hasFoodDonorsList = usePermission({
    resource: ACLService.resources.foodDonorsList,
  });
  const isDonorLinkRegistration = state?.isDonorLinkRegistration;

  useEffect(() => {
    dispatch(foodDonorsActions.fetchFoodDonorIfNeeded(locationId));
  }, []);

  useEffect(() => {
    const unblock = history.block((loc) => {
      if (loc?.state?.isDonorLinkRegistration) {
        return true;
      }

      if (!isDonorLinkRegistration) {
        return true;
      }

      confirmAlert({
        message: 'You have unsaved changes, are you sure you want to leave?',
        buttons: [
          {
            label: 'Nevermind',
            color: 'secondary',
          },
          {
            label: 'Yes',
            color: 'primary',
            onClick: () => {
              unblock();
              history.push(generatePath(loc.pathname));
            },
          },
        ],
        customUI: ({ message, onClose, buttons }) => (
          <ConfirmationDialog buttons={buttons} closeDialog={onClose} message={message} />
        ),
      });

      return false;
    });

    return () => {
      unblock();
    };
  }, [history, isDonorLinkRegistration]);

  if (!foodDonor) {
    return 'Loading...';
  }

  const onCreateFoodDonorPickupLocation = (data) =>
    Bluebird.try(() => dispatch(foodDonorsActions.createFoodDonorPickupLocation(foodDonor.id, data)))
      .then((res) => res.json())
      .then((json) => {
        notificationsHelper.addNotification('Food Donor Pickup Location added successfully!', notificationsContext);

        if (state?.donorLinkMerge) {
          return history.push(
            generatePath(routes.donor_link_with_pickup_donation_add, {
              location_id: json.data.donor_location_id,
              pickup_location_id: json.data.id,
            }),
            {
              donor_link_external_id: state?.donorLinkExternalId,
              todoItem: state?.todoItem,
              isDonorLinkRegistration: true,
            }
          );
        }

        return history.push(generatePath(routes.foodDonor, { foodDonorId: locationId }));
      });

  return (
    <>
      <Breadcrumbs sx={{ padding: 1 }} aria-label="Breadcrumbs">
        {hasFoodDonorsList ? (
          <Link color="inherit" to={routes.foodDonors}>
            Food Donors
          </Link>
        ) : (
          <Typography color="textPrimary">Food Donors</Typography>
        )}

        <Link
          color="inherit"
          to={generatePath(routes.foodDonor, { foodDonorId: locationId })}
        >
          {foodDonor.name}
        </Link>

        <Typography color="textPrimary">
          Add a Food Donor Pickup Location
        </Typography>
      </Breadcrumbs>

      <OverlayLoader isLoading={foodDonors.inflight}>
        <Paper
          sx={{
            padding: { xs: 1, sm: 2 },
            marginBottom: 3,
          }}
        >
          <FoodDonorPickupLocationAddForm
            createFoodDonorPickupLocation={onCreateFoodDonorPickupLocation}
            defaultValues={state?.pickupLocation}
            donorLinkMerge={state?.donorLinkMerge}
          />
        </Paper>
      </OverlayLoader>
    </>
  );
};

export default FoodDonorPickupLocationAddView;
